import { useMemo, useState } from "react";
import BatchDeviceList from "@/pages/admin/audit/components/BatchDeviceList";
import type { AuditLog } from "@/pages/admin/audit/hooks/useAuditLogs";

interface MonthlyCleanupArchiveProps {
  logs: AuditLog[];
  loading: boolean;
}

interface BatchEntry {
  id: number;
  batchId: string;
  time: string;
  detail: string | null;
  actor: string;
}

interface MonthGroup {
  key: string;
  label: string;
  batches: BatchEntry[];
}

const BATCH_ACTION = "批量解绑非活跃设备";

const TREND_METRICS: { key: "devices" | "occurrences"; label: string }[] = [
  { key: "devices", label: "按台数" },
  { key: "occurrences", label: "按次数" },
];

function formatDateTime(input: string): string {
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function monthKey(input: string): string {
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return "unknown";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(key: string): string {
  if (key === "unknown") return "未知月份";
  const [year, month] = key.split("-");
  return `${year} 年 ${Number(month)} 月`;
}

function monthShortLabel(key: string): string {
  if (key === "unknown") return "--";
  const [, month] = key.split("-");
  return `${Number(month)}月`;
}

// 批次清理日志的 detail 形如「产品 · 回收 12 台非活跃设备（30 天未活跃）」，从中解析回收台数。
function parseCleanedCount(detail: string | null): number {
  if (!detail) return 0;
  const match = detail.match(/回收\s*(\d+)\s*台/);
  return match ? Number(match[1]) : 0;
}

export default function MonthlyCleanupArchive({ logs, loading }: MonthlyCleanupArchiveProps) {
  const [openMonth, setOpenMonth] = useState<string | null>(null);
  const [openBatch, setOpenBatch] = useState<string | null>(null);
  const [trendMetric, setTrendMetric] = useState<"devices" | "occurrences">("devices");

  const months = useMemo<MonthGroup[]>(() => {
    const map = new Map<string, MonthGroup>();
    logs
      .filter((log) => log.action === BATCH_ACTION && Boolean(log.batch_id))
      .forEach((log) => {
        const key = monthKey(log.created_at);
        if (!map.has(key)) map.set(key, { key, label: monthLabel(key), batches: [] });
        map.get(key)?.batches.push({
          id: log.id,
          batchId: log.batch_id as string,
          time: log.created_at,
          detail: log.detail,
          actor: log.actor_name?.trim() || "系统",
        });
      });
    return Array.from(map.values())
      .map((group) => ({
        ...group,
        batches: group.batches.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime()),
      }))
      .sort((a, b) => b.key.localeCompare(a.key));
  }, [logs]);

  const totalBatches = months.reduce((sum, group) => sum + group.batches.length, 0);
  const activeMonth = openMonth ?? months[0]?.key ?? null;

  // 月度趋势：近几个月各月的清理台数与清理次数，可切换口径，并保留每月批次明细供悬停查看。
  const trend = useMemo(
    () =>
      months
        .slice(0, 6)
        .map((group) => ({
          key: group.key,
          label: monthShortLabel(group.key),
          fullLabel: group.label,
          devices: group.batches.reduce((sum, batch) => sum + parseCleanedCount(batch.detail), 0),
          count: group.batches.length,
          batches: group.batches,
        }))
        .reverse(),
    [months],
  );

  const trendValue = (item: { devices: number; count: number }) =>
    trendMetric === "devices" ? item.devices : item.count;
  const trendTotal = trend.reduce((sum, item) => sum + trendValue(item), 0);
  const trendMax = trend.reduce((max, item) => Math.max(max, trendValue(item)), 0);

  return (
    <section className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-700">
              <i className="ri-archive-2-line" />
            </span>
            月度清理归档
          </h2>
          <p className="mt-1 text-xs text-foreground-500">
            按月份归档每一次「批量解绑非活跃设备」清理，展开即可回看当月各批次回收了哪些设备，方便按月翻查与存档。
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-history-line" />
          </span>
          {loading ? "--" : `${months.length} 个月 · ${totalBatches} 次清理`}
        </span>
      </div>

      {loading ? (
        <p className="mt-4 text-xs text-foreground-500">加载中…</p>
      ) : months.length === 0 ? (
        <p className="mt-4 rounded-md border border-dashed border-background-300 bg-background-100 px-4 py-8 text-center text-xs text-foreground-500">
          暂无批量清理记录，产生「批量解绑非活跃设备」事件后会自动按月归档。
        </p>
      ) : (
        <div className="mt-4 flex flex-col gap-4">
          <div className="rounded-lg border border-background-200 bg-background-100 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="flex items-center gap-2 text-sm font-medium text-foreground-950">
                <span className="flex h-4 w-4 items-center justify-center text-accent-700">
                  <i className="ri-line-chart-line" />
                </span>
                清理趋势 · 近 {trend.length} 个月
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-0.5 rounded-full border border-background-300 bg-background-50 px-1 py-1">
                  {TREND_METRICS.map((option) => {
                    const active = trendMetric === option.key;
                    return (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => setTrendMetric(option.key)}
                        className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
                          active
                            ? "bg-primary-500 text-background-50"
                            : "text-foreground-600 hover:bg-background-100"
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-2.5 py-0.5 text-[11px] font-medium text-accent-800">
                  <span className="flex h-3 w-3 items-center justify-center">
                    <i className={trendMetric === "devices" ? "ri-delete-bin-6-line" : "ri-repeat-line"} />
                  </span>
                  {trendMetric === "devices" ? `合计回收 ${trendTotal} 台` : `合计清理 ${trendTotal} 次`}
                </span>
              </div>
            </div>
            <div className="mt-4 flex items-end justify-between gap-2">
              {trend.map((item, index) => {
                const value = trendValue(item);
                const height = value === 0 ? 4 : Math.max(10, Math.round((value / Math.max(1, trendMax)) * 88));
                const isPeak = value > 0 && value === trendMax;
                const isFirst = index === 0;
                const isLast = index === trend.length - 1;
                const posClass = isFirst ? "left-0" : isLast ? "right-0" : "left-1/2 -translate-x-1/2";
                return (
                  <div key={item.key} className="group relative flex flex-1 flex-col items-center gap-1.5">
                    <div
                      className={`pointer-events-none absolute bottom-full z-20 mb-2 hidden w-56 rounded-lg border border-background-200 bg-background-50 p-3 text-left shadow-sm group-hover:block ${posClass}`}
                    >
                      <p className="text-[11px] font-semibold text-foreground-950">{item.fullLabel}</p>
                      <p className="mt-1 text-[10px] text-foreground-500">
                        {item.count} 次清理 · 回收 {item.devices} 台
                      </p>
                      <ul className="mt-2 flex max-h-40 flex-col gap-1.5 overflow-y-auto">
                        {item.batches.map((batch) => (
                          <li key={batch.id} className="flex items-center justify-between gap-2 text-[10px]">
                            <span className="truncate text-foreground-600">{formatDateTime(batch.time)}</span>
                            <span className="shrink-0 font-mono text-foreground-400">
                              批次 {batch.batchId.slice(0, 6)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <span className="text-[11px] font-medium text-foreground-800">{value > 0 ? value : ""}</span>
                    <div className="flex h-24 w-full items-end justify-center">
                      <div
                        className={`w-full max-w-[42px] rounded-md transition-all ${
                          value === 0 ? "bg-background-200" : isPeak ? "bg-accent-500" : "bg-secondary-400"
                        }`}
                        style={{ height: `${height}px` }}
                      />
                    </div>
                    <span className="text-[11px] text-foreground-500">{item.label}</span>
                  </div>
                );
              })}
            </div>
            <p className="mt-2 text-center text-[10px] text-foreground-400">
              悬停任意月份可查看当月各批次清理明细
            </p>
          </div>

          <div className="flex flex-col gap-2.5">
            {months.map((group) => {
            const expanded = activeMonth === group.key;
            return (
              <div key={group.key} className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
                <button
                  type="button"
                  onClick={() => setOpenMonth(expanded ? "__none__" : group.key)}
                  className="flex w-full cursor-pointer items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-background-200/60"
                >
                  <span className="flex min-w-0 items-center gap-2.5">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                      <i className="ri-calendar-line" />
                    </span>
                    <span className="min-w-0">
                      <span className="block font-heading text-sm font-semibold text-foreground-950">
                        {group.label}
                      </span>
                      <span className="mt-0.5 block text-[11px] text-foreground-500">
                        共 {group.batches.length} 次批量清理
                      </span>
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <span className="rounded-full bg-primary-100 px-2.5 py-0.5 text-[11px] font-medium text-primary-700">
                      {group.batches.length} 批次
                    </span>
                    <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
                      <i className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                    </span>
                  </span>
                </button>

                {expanded && (
                  <div className="border-t border-background-200 bg-background-50 px-4 py-3">
                    <ul className="flex flex-col gap-2.5">
                      {group.batches.map((batch) => {
                        const isOpen = openBatch === batch.batchId;
                        return (
                          <li key={batch.id} className="rounded-md border border-background-200 bg-background-50">
                            <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
                              <div className="flex min-w-0 items-center gap-2">
                                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                                  <i className="ri-delete-bin-6-line text-[12px]" />
                                </span>
                                <div className="min-w-0">
                                  <p className="truncate text-xs font-medium text-foreground-900">
                                    {batch.detail || "批量解绑非活跃设备"}
                                  </p>
                                  <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[10px] text-foreground-500">
                                    <span>{formatDateTime(batch.time)}</span>
                                    <span className="font-mono text-foreground-400">
                                      批次 {batch.batchId.slice(0, 8)}
                                    </span>
                                    <span>· {batch.actor}</span>
                                  </p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => setOpenBatch(isOpen ? null : batch.batchId)}
                                title="展开查看本次回收的设备"
                                className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full bg-accent-100 px-2.5 py-0.5 text-[10px] font-medium text-accent-800 transition-colors hover:bg-accent-200"
                              >
                                <span className="flex h-3 w-3 items-center justify-center">
                                  <i className={isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                                </span>
                                {isOpen ? "收起设备" : "查看回收设备"}
                              </button>
                            </div>
                            {isOpen && (
                              <div className="px-3 pb-3">
                                <BatchDeviceList batchId={batch.batchId} />
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
          </div>
        </div>
      )}
    </section>
  );
}